import React from "react";
import PropTypes from "prop-types";
import { Alert, AlertDescription, Link } from "@chakra-ui/react";
import { AlertIcon } from "../../Components/LucidePrimitives";

/*
 * Next steps for a failed badge-provider connection, keyed on the backend's
 * machine-readable `error` code (see controllers/badges.py:_oauth_failure).
 */
export default function OAuthFailureAlert({ failure, provider }) {
  let logoutUrl = null;
  try {
    logoutUrl = new URL(provider.oauth_url).origin + "/auth/logout";
  } catch (e) {
    logoutUrl = null;
  }
  const name = provider.display_name || "Parchment Digital Badges";

  let body;
  if (failure.error === "email_conflict") {
    body = (
      <>
        {failure.message}{" "}
        On a shared computer, someone else may still be signed in to {name}.{" "}
        {logoutUrl ? (
          <Link href={logoutUrl} isExternal textDecoration="underline">
            Sign out of {name}
          </Link>
        ) : (
          `Sign out of ${name}`
        )}
        , then connect again, or connect from your own phone. If this is
        your account, contact space staff.
      </>
    );
  } else if (failure.error === "not_returned") {
    body = `It looks like ${name} didn't send you back to Pinpoint last time, so your account wasn't connected. Click Connect again. If you're already signed in to ${name}, it should go straight through.`;
  } else if (failure.error === "session_required") {
    body = "Your Pinpoint session expired before the connection finished. Please connect again.";
  } else if (failure.error === "no_verified_emails") {
    body = `Your ${name} account has no verified email address. Verify your email in ${name}, then connect again.`;
  } else if (failure.error === "user_not_found") {
    body = "Your Pinpoint account could not be found, so the connection was not saved. Sign out and sign in again; if this keeps happening, contact space staff.";
  } else {
    body = `${name} couldn't complete the connection right now. Please try again in a few minutes. If it keeps failing, contact space staff.`;
  }

  return (
    <Alert
      status={failure.error === "not_returned" ? "warning" : "error"}
      rounded="md"
      mb={4}
      alignItems="flex-start"
    >
      <AlertIcon />
      <AlertDescription fontSize="sm">{body}</AlertDescription>
    </Alert>
  );
}
OAuthFailureAlert.propTypes = {
  failure: PropTypes.shape({
    error: PropTypes.string,
    message: PropTypes.string,
  }).isRequired,
  provider: PropTypes.shape({
    oauth_url: PropTypes.string,
    display_name: PropTypes.string,
  }).isRequired,
};
